import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/radar";
import { toast } from "sonner";
import { Archive, RotateCcw, Trash2, ShieldAlert, ClipboardList, Plus } from "lucide-react";

const TILES = [
  { key: "live_total", label: "Live companies", tone: "text-slate-900" },
  { key: "live_real", label: "Real (non-synthetic)", tone: "text-teal-700" },
  { key: "archived_total", label: "Archived", tone: "text-amber-600" },
  { key: "dsr_open", label: "Open data requests", tone: "text-red-600" },
];

const DSR_TYPES = ["access", "correction", "deletion", "other"];

export default function Retention() {
  const [summary, setSummary] = useState<any>(null);
  const [archived, setArchived] = useState<any[]>([]);
  const [dsr, setDsr] = useState<any[]>([]);
  const [purgeTarget, setPurgeTarget] = useState<any>(null);
  const [dsrOpen, setDsrOpen] = useState(false);
  const [dsrForm, setDsrForm] = useState<any>({ subject_name: "", subject_email: "", company_name: "", request_type: "access", note: "" });

  const loadAll = async () => {
    try {
      const [s, a, d] = await Promise.all([
        api.get("/retention/summary"), api.get("/companies/archived"), api.get("/dsr"),
      ]);
      setSummary(s.data); setArchived(a.data); setDsr(d.data);
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); }, []);

  const restore = async (c: any) => {
    try { await api.post(`/companies/${c.id}/restore`); toast.success(`${c.company_name} restored`); loadAll(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const purge = async () => {
    try {
      await api.delete(`/companies/${purgeTarget.id}/purge`);
      toast.success(`${purgeTarget.company_name} permanently deleted`);
      setPurgeTarget(null); loadAll();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createDsr = async () => {
    if (!dsrForm.subject_name.trim()) { toast.error("Subject name is required"); return; }
    try {
      await api.post("/dsr", dsrForm);
      toast.success("Data request logged");
      setDsrOpen(false);
      setDsrForm({ subject_name: "", subject_email: "", company_name: "", request_type: "access", note: "" });
      loadAll();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setDsrStatus = async (id: string, status: string) => {
    try { await api.patch(`/dsr/${id}`, { status }); toast.success(`Marked ${status}`); loadAll(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="retention-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><Archive className="h-5 w-5 text-teal-700" /> Retention &amp; Data Governance</h1>
        <p className="text-sm text-slate-500">Review archived records, permanently delete when retention allows, and track data-subject requests. Permanent deletion cannot be undone.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {TILES.map((t) => (
          <Card key={t.key} className="p-4 border-slate-200 shadow-none" data-testid={`tile-${t.key}`}>
            <div className="text-xs text-slate-500">{t.label}</div>
            <div className={`text-2xl font-bold num mt-1 ${t.tone}`}>{summary ? summary[t.key] : "—"}</div>
          </Card>
        ))}
      </div>

      {/* Archived companies */}
      <Card className="p-4 border-slate-200 shadow-none">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Archive className="h-4 w-4" /> Archived companies</h3>
        <div className="mt-3 divide-y divide-slate-100" data-testid="archived-list">
          {archived.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No archived companies.</div>}
          {archived.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2.5 text-sm gap-2" data-testid={`retention-row-${c.id}`}>
              <div className="min-w-0">
                <span className="font-medium text-slate-800">{c.company_name}</span>
                {c.is_synthetic && <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Synthetic</span>}
                <span className="text-[11px] text-slate-400 ml-2">{c.industry || "—"}{c.deleted_at && ` · archived ${fmtDate(c.deleted_at)}`}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => restore(c)} data-testid={`restore-${c.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore</Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setPurgeTarget(c)} data-testid={`purge-${c.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Data-subject requests */}
      <Card className="p-4 border-slate-200 shadow-none">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Data-subject requests</h3>
          <Button size="sm" onClick={() => setDsrOpen(true)} className="bg-[#0B1C3D] hover:bg-[#122a55]" data-testid="dsr-new-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Log request</Button>
        </div>
        <div className="mt-3 divide-y divide-slate-100" data-testid="dsr-list">
          {dsr.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No data-subject requests logged.</div>}
          {dsr.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5 text-sm gap-2" data-testid={`dsr-row-${r.id}`}>
              <div className="min-w-0">
                <span className="font-medium text-slate-800">{r.subject_name}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{r.request_type}</span>
                {r.company_name && <span className="text-[11px] text-slate-400 ml-2">· {r.company_name}</span>}
                {r.note && <div className="text-[12px] text-slate-500 truncate max-w-xl">{r.note}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[11px] px-2 py-0.5 rounded-md border ${r.status === "open" ? "bg-amber-50 text-amber-700 border-amber-200" : r.status === "actioned" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{r.status}</span>
                <Select value={r.status} onValueChange={(v) => setDsrStatus(r.id, v)}>
                  <SelectTrigger className="h-8 w-32" data-testid={`dsr-status-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="actioned">Actioned</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Permanent delete confirm */}
      <Dialog open={!!purgeTarget} onOpenChange={(v) => { if (!v) setPurgeTarget(null); }}>
        <DialogContent data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-600" /> Permanently delete company</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{purgeTarget?.company_name}</span> and all its evidence, signals, scores and activity will be permanently removed. This cannot be undone. The audit trail is retained.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeTarget(null)}>Cancel</Button>
            <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={purge} data-testid="purge-confirm">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New DSR */}
      <Dialog open={dsrOpen} onOpenChange={setDsrOpen}>
        <DialogContent data-testid="dsr-dialog">
          <DialogHeader>
            <DialogTitle>Log a data-subject request</DialogTitle>
            <DialogDescription>Record an access, correction or deletion request from an individual or company.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Subject name *</Label>
              <Input value={dsrForm.subject_name} onChange={(e) => setDsrForm({ ...dsrForm, subject_name: e.target.value })} data-testid="dsr-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Subject email</Label>
                <Input value={dsrForm.subject_email} onChange={(e) => setDsrForm({ ...dsrForm, subject_email: e.target.value })} data-testid="dsr-email" />
              </div>
              <div>
                <Label>Related company</Label>
                <Input value={dsrForm.company_name} onChange={(e) => setDsrForm({ ...dsrForm, company_name: e.target.value })} data-testid="dsr-company" />
              </div>
            </div>
            <div>
              <Label>Request type</Label>
              <Select value={dsrForm.request_type} onValueChange={(v) => setDsrForm({ ...dsrForm, request_type: v })}>
                <SelectTrigger data-testid="dsr-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DSR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={dsrForm.note} onChange={(e) => setDsrForm({ ...dsrForm, note: e.target.value })} data-testid="dsr-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDsrOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={createDsr} data-testid="dsr-submit">Log request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
